import { ComponentType, Key } from "react";
import { Control, Controller, DeepMap, FieldError } from "react-hook-form";
import ReactSelect, {
  ActionMeta,
  components as Components,
  createFilter,
  CSSObjectWithLabel,
  GroupBase,
  GroupHeadingProps,
  MultiValue,
  MultiValueGenericProps,
  OptionProps,
  OptionsOrGroups,
  SingleValue,
} from "react-select";
import Creatable from "react-select/creatable";
import { WindowedMenuList } from "react-windowed-select";
import { Label } from "semantic-ui-react";
import { errorMessageSelector } from "../../../../demo/components/filters/Utils";
import { Readonly } from "../../../ControlledForm";
import { useId } from "react-aria";

export type AccessibleDropdownOption<T = any> = {
  key?: string | number;
  value: T;
  label: string;
  color?: string;
  isDisabled?: boolean;
  isFixed?: boolean;
};

interface IControlledAccessibleDropdownStyles {
  control: CSSObjectWithLabel;
}

/**
 * The components of the dropdown can be swapped out for custom components. For a full list, see the link
 * @see https://react-select.com/components#replaceable-components
 *
 * For the default values of the components, see the MultiValueLabel example. The defaults for the other
 * components should be in the same place.
 */
interface IControlledAccessibleDropdownComponents {
  multiValueLabel?: ComponentType<
    MultiValueGenericProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
  >;
  groupHeading?: ComponentType<
    GroupHeadingProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>
  >;
  option?: ComponentType<OptionProps<AccessibleDropdownOption, boolean, GroupBase<AccessibleDropdownOption>>>;
}

interface AccessibleDropdown {
  placeholder?: string;
  clearable?: boolean;
  options: OptionsOrGroups<AccessibleDropdownOption, GroupBase<AccessibleDropdownOption>>;
  multiple?: boolean;
  defaultValue?: AccessibleDropdownOption["value"] | null | any;
  searchByGroup?: boolean;
  useVirtualList?: boolean;
  rules?: any;
  readonly?: boolean;
  transform?: {
    input: (value: any) => AccessibleDropdownOption | AccessibleDropdownOption[] | null;
    output: (value: AccessibleDropdownOption | readonly AccessibleDropdownOption[] | null) => any;
  };
  components?: IControlledAccessibleDropdownComponents;
  styles?: IControlledAccessibleDropdownStyles;
  maxMenuHeight?: number;
  hideBorder?: boolean;
  allowAdditions?: boolean;
  onCreateOption?: (inputValue: string) => void;
  disabled?: boolean;
  className?: string;
  classNamePrefix?: string;
  label?: string;
  subLabel?: string;
}

interface IAccessibleDropdown extends AccessibleDropdown {
  name?: string;
  onChange?: (newValue: MultiValue<AccessibleDropdownOption> | SingleValue<AccessibleDropdownOption>) => void;
  value: AccessibleDropdownOption["value"] | AccessibleDropdownOption["value"][] | null;
  id?: string;
  error?: string;
  showErrorLabel?: boolean;
  virtualDropdownProps?: AccessibleDropdown;
}

export function AccessibleDropdown({
  name,
  value,
  options,
  placeholder,
  clearable = false,
  multiple = false,
  defaultValue,
  searchByGroup = false,
  useVirtualList = true,
  transform = { input: (value) => value, output: (value) => value },
  onChange = () => {},
  components,
  styles,
  maxMenuHeight,
  hideBorder = false,
  allowAdditions = false,
  onCreateOption,
  disabled = false,
  className = "",
  classNamePrefix = "",
  id,
  error,
  showErrorLabel = true,
  label,
  subLabel,
  readonly = false,
}: IAccessibleDropdown) {
  const finalId = useId(id);

  const Component = allowAdditions ? Creatable : ReactSelect;

  const filterByGroupsAndOptions = ({ label, value }: { label: string; value: string }, query: string) => {
    const labelLowerCase = label.toLowerCase();
    const valueLowerCase = value.toLowerCase();
    const queryLowerCase = query.toLowerCase();

    if (labelLowerCase.includes(queryLowerCase) || valueLowerCase.includes(queryLowerCase)) return true;

    const matchingOptionsAndGroups = options.filter((optionOrGroup) =>
      (optionOrGroup as GroupBase<AccessibleDropdownOption>).label?.toLowerCase().includes(queryLowerCase),
    );

    if (!!matchingOptionsAndGroups.length) {
      for (const optionOrGroup of matchingOptionsAndGroups) {
        const group = optionOrGroup as GroupBase<AccessibleDropdownOption>;
        const isGroup = !!group.options;
        if (isGroup) {
          const option = group.options.find(
            (option) => option.value.toLowerCase() === valueLowerCase || option.label.toLowerCase() === labelLowerCase,
          );
          if (!!option) return true;
        }
      }
    }
    return false;
  };

  return (
    <>
      {!!label && <label htmlFor={finalId}>{label}</label>}
      {subLabel && <div className='-tw-mt-1 tw-mb-1 tw-text-sm tw-text-grey'>{subLabel}</div>}
      {readonly && !multiple && <Readonly as='span' value={transform.input(value ?? defaultValue)} />}
      {readonly && multiple && (
        <Readonly
          className={className}
          as='span'
          value={(transform.input(value ?? defaultValue) as AccessibleDropdownOption[] | null)
            ?.map((option) => {
              return option.label;
            })
            .join(" • ")}
        />
      )}
      {!readonly && (
        <Component
          inputId={finalId}
          className={className}
          classNamePrefix={classNamePrefix}
          createOptionPosition='first'
          formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
          isDisabled={disabled}
          onCreateOption={(inputValue) => {
            onCreateOption && onCreateOption(inputValue);
          }}
          maxMenuHeight={maxMenuHeight}
          unstyled
          components={{
            MultiValueLabel:
              components?.multiValueLabel !== undefined ? components.multiValueLabel : Components.MultiValueLabel,
            DropdownIndicator: () => <i style={{ width: "6px", height: "18px" }} className='dropdown icon'></i>,
            ...(useVirtualList ? { MenuList: WindowedMenuList } : {}),
            GroupHeading: components?.groupHeading !== undefined ? components.groupHeading : Components.GroupHeading,
            Option: components?.option !== undefined ? components.option : Components.Option,
          }}
          classNames={{
            multiValue: () =>
              "ui label basic tw-m-0 tw-py-1 tw-h-[20px] tw-px-2 tw-space-x-1 tw-bg-[#E8E8E8] tw-border-border tw-text-[#00000099]",
          }}
          styles={{
            control: (base, props) => ({
              ...base,
              "paddingRight": "14px",
              "display": "flex",
              "color": "rgba(0, 0, 0, 0.87)",
              "border": hideBorder ? 0 : "1px solid rgba(34, 36, 38, 0.15)",
              "backgroundColor": !!error ? "#FFF6F6" : "#FFFFFF",
              "borderRadius": props.menuIsOpen ? "0.28571429rem 0.28571429rem 0 0" : "0.28571429rem",
              "borderColor": !!error ? "#E0B4B4" : props.menuIsOpen ? "rgba(34, 36, 38, 0.15)" : undefined,
              "boxShadow": props.menuIsOpen ? "0px 2px 3px 0px rgba(34, 36, 38, 0.15)" : undefined,
              "borderBottomColor": props.menuIsOpen ? "rgba(0, 0, 0, 0)" : undefined,
              "transition": "none",
              ":hover": {
                cursor: "text",
              },
              ...(styles && styles?.control),
            }),
            valueContainer: (base, props) => ({
              ...base,
              color: "rgba(0, 1, 0, 0.87)",
              overflow: "auto",
              padding: "8px 0 8px 0",
              paddingLeft: props.isMulti && props.hasValue ? "7px" : "16px",
              gap: "3px",
            }),
            indicatorsContainer: (base) => ({
              ...base,
              "gap": "2px",
              "color": "rgba(0, 1, 0, 0.87)",
              ":hover": {
                cursor: "pointer",
              },
            }),
            menu: (base) => ({
              ...base,
              backgroundColor: "white",
              borderLeft: hideBorder ? 0 : "1px solid rgba(34, 36, 38, 0.15)",
              borderRight: hideBorder ? 0 : "1px solid rgba(34, 36, 38, 0.15)",
              borderBottom: hideBorder ? 0 : "1px solid rgba(34, 36, 38, 0.15)",
              borderColor: "#96C8DA",
              borderRadius: "0 0 0.28571429rem 0.28571429rem",
              boxShadow: "0px 2px 3px 0px rgba(34, 36, 38, 0.15)",
              marginTop: "-1px",
              overflow: "auto",
              zIndex: 3,
            }),
            option: (base, props) => {
              return {
                ...base,
                "padding": "8px 16px",
                "transition": "0.3s",
                "backgroundColor": (props.data as AccessibleDropdownOption)?.color
                  ? (props.data as AccessibleDropdownOption)?.color
                  : props.isSelected
                    ? "#dcdfec"
                    : props.isFocused
                      ? "rgba(0, 0, 0, 0.03)"
                      : undefined,
                "borderTop": "1px solid #FAFAFA",
                "color": props.isDisabled ? "rgba(0, 0, 0, 0.4)" : undefined,
                ":hover": {
                  cursor: "pointer",
                  backgroundColor: !(props.data as AccessibleDropdownOption)?.color ? "#dcdfec" : undefined,
                  filter: (props.data as AccessibleDropdownOption)?.color ? "brightness(80%)" : undefined,
                },
              };
            },
            groupHeading: (base) => ({
              ...base,
              textTransform: "uppercase",
              color: "rgba(0, 0, 0, 0.85)",
              fontSize: "0.78571429em",
              fontWeight: "bold",
              padding: "16px 0px 4px 16px",
            }),
            multiValue: (base) => ({
              ...base,
              display: "flex !important",
              alignItems: "center",
              cursor: "default",
            }),
            multiValueRemove: (base, state) => ({
              ...base,
              cursor: "pointer",
              ...(state.data.isFixed ? { display: "none" } : {}),
            }),
            placeholder: (base) => ({
              ...base,
              color: "rgba(191, 191, 191, 0.87)",
            }),
            noOptionsMessage: (base) => ({
              ...base,
              padding: "8px 16px",
              borderTop: "1px solid #FAFAFA",
            }),
          }}
          isClearable={clearable}
          isSearchable
          value={transform.input(value)}
          onChange={(
            newValue: MultiValue<AccessibleDropdownOption<any>> | SingleValue<AccessibleDropdownOption<any>> | null,
            actionMeta: ActionMeta<AccessibleDropdownOption<any>>,
          ) => {
            const actionIsRemove = actionMeta.action === "remove-value" || actionMeta.action === "pop-value";
            const shouldSuppressAction = actionIsRemove && actionMeta.removedValue.isFixed;
            if (shouldSuppressAction) return;

            const actionIsClear = actionMeta.action === "clear";
            if (actionIsClear && multiple) {
              // Remove all values except fixed ones
              const removedFixedValues = actionMeta.removedValues.filter((value) => value.isFixed);
              const newValueWithFixedValuesReplaced = [
                ...removedFixedValues,
                ...(newValue as AccessibleDropdownOption[]),
              ];
              onChange(transform.output(newValueWithFixedValuesReplaced));
              return;
            }

            if (newValue !== null) {
              onChange(transform.output(newValue));
              return;
            }
            onChange(transform.output(multiple ? [] : null));
          }}
          name={name}
          options={options}
          isMulti={multiple}
          filterOption={
            !!searchByGroup
              ? filterByGroupsAndOptions
              : createFilter({ ignoreAccents: false, stringify: (option) => option.label })
          }
          defaultValue={transform.input(defaultValue)}
          placeholder={placeholder}
        />
      )}
      {error && showErrorLabel && (
        <Label basic prompt pointing='above'>
          {error}
        </Label>
      )}
    </>
  );
}

interface IControlledAccessibleDropdown extends AccessibleDropdown {
  control: Control;
  name: string;
  errors?: DeepMap<Record<Key, unknown>, FieldError>;
}

export function ControlledAccessibleDropdown({
  name,
  control,
  options,
  label,
  subLabel,
  placeholder,
  errors,
  clearable = false,
  multiple = false,
  defaultValue,
  searchByGroup = false,
  useVirtualList = true,
  rules,
  readonly = false,
  transform = { input: (value) => value, output: (value) => value },
  onCreateOption = () => {},
  components,
  styles,
  maxMenuHeight,
  hideBorder = false,
  allowAdditions = false,
  disabled = false,
  className = "",
  classNamePrefix = "",
}: IControlledAccessibleDropdown) {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ onChange, value, name }) => {
        return (
          <AccessibleDropdown
            name={name}
            options={options}
            placeholder={placeholder}
            onChange={(value) => onChange(transform.output(value))}
            components={components}
            styles={styles}
            maxMenuHeight={maxMenuHeight}
            hideBorder={hideBorder}
            allowAdditions={allowAdditions}
            onCreateOption={onCreateOption}
            disabled={disabled}
            className={className}
            classNamePrefix={classNamePrefix}
            value={value}
            clearable={clearable}
            multiple={multiple}
            defaultValue={defaultValue}
            searchByGroup={searchByGroup}
            useVirtualList={useVirtualList}
            transform={transform}
            id={name}
            error={errorMessageSelector(errors)(name)}
            label={label}
            subLabel={subLabel}
            readonly={readonly}
          />
        );
      }}
    />
  );
}
